import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  UploadFile
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { FormInstance } from 'antd/lib'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'

import { useCounterpartServiceGetInternalInstitutionEndpoint } from 'src/api/queries'
import { GetContractResponse } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { CustomItemRender, Text, Upload } from 'src/components'
import {
  FundingSourceSelect,
  PharmaceuticsTable,
  ConsumablesTable
} from 'src/features/other-institutions/components'
import { getTotalTvaPrice } from 'src/features/stock'
import { FileDocument, onDownloadFile, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

const { Item } = Form

type Props = {
  data?: GetContractResponse
  form: FormInstance
  onChange?: (info: UploadChangeParam<UploadFile>) => void
}

export const AddContractForm = ({ form, data, onChange }: Props) => {
  const { t } = useLocales()
  const { userData } = useUser()

  const { data: institution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(userData?.currentLegalEntityId) },
      [String(userData?.currentLegalEntityId)],
      { enabled: !!userData?.currentLegalEntityId }
    )

  const medicineItems = Form.useWatch('medicine', form)
  const consumables = Form.useWatch('consumables', form)

  const total = (medicineItems ?? [])
    .concat(consumables ?? [])
    .reduce(
      (
        acc: number,
        item: { quantity: number; unitPrice: number; tvaUnitRateName: string }
      ) => {
        if (!item) return acc

        const totalTvaPrice = getTotalTvaPrice(
          item.quantity,
          item.unitPrice,
          item.tvaUnitRateName
        )

        return acc + (typeof totalTvaPrice === 'number' ? totalTvaPrice : 0)
      },
      0
    )

  useEffect(() => {
    if (institution) {
      form.setFieldsValue({
        beneficiaryId: institution.id,
        beneficiaryName: institution.name
      })
    }
  }, [institution])

  return (
    <Card>
      <Flex vertical gap={16}>
        <Form.Item shouldUpdate>
          {({ getFieldValue }) => {
            const isStartDateDisabled = !getFieldValue('signedOn')
            const isEndDateDisabled = !getFieldValue('startDate')

            const disabledStartDate = (current: Dayjs) => {
              return (
                current &&
                current < dayjs(getFieldValue('signedOn')).startOf('day')
              )
            }

            const disabledEndDate = (current: Dayjs) => {
              return (
                current &&
                current < dayjs(getFieldValue('startDate')).endOf('day')
              )
            }

            return (
              <Row gutter={[24, 24]}>
                <Item name="beneficiaryId" hidden />

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="beneficiaryName"
                    label={t('stock_applications.beneficiary')}
                    rules={requiredRules}
                  >
                    <Input disabled />
                  </Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="contractNumber"
                    label={t('counterparts.contract_number')}
                    rules={requiredRules}
                  >
                    <Input
                      placeholder={t('counterparts.contract_number')}
                      className="full-width"
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="signedOn"
                    label={t('counterparts.signed_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      placeholder={t('counterparts.signed_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="startDate"
                    label={t('counterparts.contract_start_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      disabled={isStartDateDisabled}
                      disabledDate={disabledStartDate}
                      className="full-width"
                      placeholder={t('counterparts.contract_start_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="endDate"
                    label={t('counterparts.contract_end_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      disabled={isEndDateDisabled}
                      disabledDate={disabledEndDate}
                      className="full-width"
                      placeholder={t('counterparts.contract_end_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="fundingSourceId"
                    label={t('menu.funding_source')}
                    rules={requiredRules}
                  >
                    <FundingSourceSelect />
                  </Item>
                </Col>

                <Divider />

                <Form.List name="medicine">
                  {(fields, { add, remove }) => (
                    <ConsumablesTable
                      item={data}
                      fields={fields}
                      add={add}
                      remove={remove}
                      form={form}
                    />
                  )}
                </Form.List>

                <Form.List name="consumables">
                  {(fields, { add, remove }) => (
                    <PharmaceuticsTable
                      item={data}
                      fields={fields}
                      add={add}
                      remove={remove}
                      form={form}
                    />
                  )}
                </Form.List>

                <Flex
                  justify="end"
                  align="center"
                  gap={8}
                  className="full-width"
                >
                  <Text weight={700}>{t('common.total')}: </Text>
                  <Text weight={700}>
                    {parseFloat(total ?? '0')?.toFixed(2)} MDL
                  </Text>
                </Flex>

                <Divider />

                <Item
                  name="docs"
                  className="full-width"
                  label={t('counterparts.additional_files')}
                  rules={requiredRules}
                >
                  <Upload
                    defaultFileList={
                      data?.files?.map((file) => ({
                        ...file,
                        uid: file.id,
                        name: file.name
                      })) as UploadFile[]
                    }
                    onChange={onChange}
                    customRequest={() => null}
                    itemRender={(_, file: FileDocument, __, { remove }) => {
                      const isExistingFile = data?.files?.some(
                        (f) => f.id === file.id
                      )

                      return (
                        <CustomItemRender
                          file={file}
                          onRemove={remove}
                          onDownload={
                            isExistingFile
                              ? () => onDownloadFile(t, file.id, file.name)
                              : undefined
                          }
                        />
                      )
                    }}
                  />
                </Item>

                <Divider />

                <Item
                  name="description"
                  className="full-width"
                  label={t('counterparts.description')}
                >
                  <Input.TextArea
                    rows={5}
                    placeholder={t('counterparts.description')}
                  />
                </Item>
              </Row>
            )
          }}
        </Form.Item>
      </Flex>
    </Card>
  )
}
